import React from "react";
import { Control, Controller, FieldErrors, FieldArrayWithId, UseFormSetValue, useWatch } from "react-hook-form";
import { FormSection } from "./FormSection";
import { Button, Combobox, Field, Input, Switch, Label, Dialog } from "@/components/ui";
import { Trash2, Plus, Layout, ArrowUpDown, GripVertical } from "lucide-react";
import { FormFieldType, FIELD_TYPE_MAP } from "@/services/bedrock";
import { FormSchemaType } from "../FormEditView";
import { cn } from "@/lib/utils";
import { ChoicesManager } from "./ChoicesManager";
import { StarFieldControls } from "./StarFieldControls";

const FIELD_TYPE_COLOR_MAP: Record<
  FormFieldType,
  {
    text: string;
    bg: string;
    border: string;
    hoverBg: string;
    hoverBorder: string;
    accent: string;
    icon: string;
  }
> = {
  text: {
    text: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-50 dark:bg-blue-950/20",
    border: "border-blue-200 dark:border-blue-900/30",
    hoverBg: "hover:bg-blue-100 dark:hover:bg-blue-900/20",
    hoverBorder: "hover:border-blue-300 dark:hover:border-blue-800/40",
    accent: "border-l-blue-500",
    icon: "text-blue-500",
  },
  long_text: {
    text: "text-indigo-600 dark:text-indigo-400",
    bg: "bg-indigo-50 dark:bg-indigo-950/20",
    border: "border-indigo-200 dark:border-indigo-900/30",
    hoverBg: "hover:bg-indigo-100 dark:hover:bg-indigo-900/20",
    hoverBorder: "hover:border-indigo-300 dark:hover:border-indigo-800/40",
    accent: "border-l-indigo-500",
    icon: "text-indigo-500",
  },
  single_choice: {
    text: "text-purple-600 dark:text-purple-400",
    bg: "bg-purple-50 dark:bg-purple-950/20",
    border: "border-purple-200 dark:border-purple-900/30",
    hoverBg: "hover:bg-purple-100 dark:hover:bg-purple-900/20",
    hoverBorder: "hover:border-purple-300 dark:hover:border-purple-800/40",
    accent: "border-l-purple-500",
    icon: "text-purple-500",
  },
  boolean: {
    text: "text-pink-600 dark:text-pink-400",
    bg: "bg-pink-50 dark:bg-pink-950/20",
    border: "border-pink-200 dark:border-pink-900/30",
    hoverBg: "hover:bg-pink-100 dark:hover:bg-pink-900/20",
    hoverBorder: "hover:border-pink-300 dark:hover:border-pink-800/40",
    accent: "border-l-pink-500",
    icon: "text-pink-500",
  },
  state: {
    text: "text-cyan-600 dark:text-cyan-400",
    bg: "bg-cyan-50 dark:bg-cyan-950/20",
    border: "border-cyan-200 dark:border-cyan-900/30",
    hoverBg: "hover:bg-cyan-100 dark:hover:bg-cyan-900/20",
    hoverBorder: "hover:border-cyan-300 dark:hover:border-cyan-800/40",
    accent: "border-l-cyan-500",
    icon: "text-cyan-500",
  },
  star: {
    text: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-950/20",
    border: "border-amber-200 dark:border-amber-900/30",
    hoverBg: "hover:bg-amber-100 dark:hover:bg-amber-900/20",
    hoverBorder: "hover:border-amber-300 dark:hover:border-amber-800/40",
    accent: "border-l-amber-500",
    icon: "text-amber-500",
  },
  nps: {
    text: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-950/20",
    border: "border-emerald-200 dark:border-emerald-900/30",
    hoverBg: "hover:bg-emerald-100 dark:hover:bg-emerald-900/20",
    hoverBorder: "hover:border-emerald-300 dark:hover:border-emerald-800/40",
    accent: "border-l-emerald-500",
    icon: "text-emerald-500",
  },
  ten_point: {
    text: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-50 dark:bg-orange-950/20",
    border: "border-orange-200 dark:border-orange-900/30",
    hoverBg: "hover:bg-orange-100 dark:hover:bg-orange-900/20",
    hoverBorder: "hover:border-orange-300 dark:hover:border-orange-800/40",
    accent: "border-l-orange-500",
    icon: "text-orange-500",
  },
  sentiment: {
    text: "text-rose-600 dark:text-rose-400",
    bg: "bg-rose-50 dark:bg-rose-950/20",
    border: "border-rose-200 dark:border-rose-900/30",
    hoverBg: "hover:bg-rose-100 dark:hover:bg-rose-900/20",
    hoverBorder: "hover:border-rose-300 dark:hover:border-rose-800/40",
    accent: "border-l-rose-500",
    icon: "text-rose-500",
  },
};

const dropdownItems = [
  "__group_header__Input Fields",
  "text",
  "long_text",
  "single_choice",
  "boolean",
  "state",
  "__group_header__Feedback & Ratings",
  "star",
  "nps",
  "ten_point",
  "sentiment",
];

interface FieldsSectionProps {
  control: Control<FormSchemaType>;
  fields: FieldArrayWithId<FormSchemaType, "formFields", "id">[];
  addField: () => void;
  remove: (index: number) => void;
  errors: FieldErrors<FormSchemaType>;
  setValue: UseFormSetValue<FormSchemaType>;
}

export function FieldsSection({ control, fields, addField, remove, errors, setValue }: FieldsSectionProps) {
  const [isReorderOpen, setIsReorderOpen] = React.useState(false);
  const [localFields, setLocalFields] = React.useState<any[]>([]);
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);

  const formFieldsValue = useWatch({
    control,
    name: "formFields",
  });

  React.useEffect(() => {
    if (isReorderOpen) {
      setLocalFields([...fields]);
    }
  }, [isReorderOpen, fields]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...localFields];
    const draggedItem = updated[draggedIndex];
    updated.splice(draggedIndex, 1);
    updated.splice(index, 0, draggedItem);
    setDraggedIndex(index);
    setLocalFields(updated);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleApplyOrder = () => {
    setValue("formFields", localFields, { shouldDirty: true });
    setIsReorderOpen(false);
  };

  return (
    <>
      <FormSection
        id="section-fields"
        title="Form Fields"
        headerActions={
          <div className="flex items-center gap-2">
            {fields.length > 1 && (
              <Button variant="outline" size="sm" type="button" onClick={() => setIsReorderOpen(true)}>
                <ArrowUpDown className="mr-2 h-4 w-4" />
                Reorder
              </Button>
            )}
            <Button variant="outline" size="sm" type="button" onClick={addField}>
              <Plus className="mr-2 h-4 w-4" />
              Add Field
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {fields.map((field, idx) => {
            const fieldError = errors.formFields?.[idx];
            const currentFieldType = formFieldsValue?.[idx]?.fieldType || field.fieldType;
            const colors = FIELD_TYPE_COLOR_MAP[currentFieldType as FormFieldType];

            return (
              <div
                key={field.id}
                className={cn(
                  "group relative rounded-xl border border-slate-200 bg-slate-50/50 p-5 shadow-sm hover:shadow-md transition-all duration-200 border-l-4",
                  colors ? colors.accent : "border-l-primary/60"
                )}
              >
                {/* Card Header: Question Number & Delete Action */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 mb-4 gap-3">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <Controller
                      name={`formFields.${idx}.fieldType`}
                      control={control}
                      render={({ field: selectField }) => (
                        <div
                          className={cn(
                            "group/pill flex items-center rounded-full h-7 text-[11px] font-bold pl-3 pr-0.5 shadow-2xs hover:shadow-xs transition-all select-none border",
                            fieldError?.fieldType
                              ? "border-destructive bg-destructive/10 hover:bg-destructive/15 hover:border-destructive/30 text-destructive"
                              : colors
                              ? cn(colors.bg, colors.border, colors.hoverBg, colors.hoverBorder, colors.text)
                              : "bg-primary/10 hover:bg-primary/15 border-primary/20 hover:border-primary/30 text-primary"
                          )}
                        >
                          <span className="uppercase tracking-wider shrink-0 pr-2">Question {idx + 1}</span>
                          <div className={cn("h-3.5 w-px shrink-0 bg-current/25")} />
                          <Combobox<FormFieldType>
                            value={selectField.value as FormFieldType}
                            onValueChange={val => {
                              selectField.onChange(val);

                              // Automatically change the field_key if it matches a default field_type pattern (e.g., text or text_2)
                              const currentKey = formFieldsValue?.[idx]?.fieldKey || "";
                              const defaultKeysRegex =
                                /^(nps|ten_point|star|text|single_choice|boolean|sentiment|state)(_\d+)?$/;

                              if (defaultKeysRegex.test(currentKey)) {
                                const otherFields = (formFieldsValue || []).filter(
                                  (f, fIdx) => fIdx !== idx && f.fieldType === val
                                );
                                const otherCount = otherFields.length;
                                const newKey = otherCount === 0 ? val : `${val}_${otherCount + 1}`;
                                setValue(`formFields.${idx}.fieldKey`, newKey, { shouldDirty: true });
                              }

                              if (val === "single_choice") {
                                const currentChoices = formFieldsValue?.[idx]?.fieldOptions?.choices;
                                if (!currentChoices || currentChoices.length === 0) {
                                  setValue(`formFields.${idx}.fieldOptions.choices`, ["Option 1", "Option 2"], {
                                    shouldDirty: true,
                                  });
                                }
                              } else if (val === "star") {
                                const currentMode = formFieldsValue?.[idx]?.fieldOptions?.collectionMode;
                                if (!currentMode) {
                                  setValue(`formFields.${idx}.fieldOptions.collectionMode`, "hybrid", {
                                    shouldDirty: true,
                                  });
                                  setValue(`formFields.${idx}.fieldOptions.handoffTiming`, "on_completion", {
                                    shouldDirty: true,
                                  });
                                  setValue(
                                    `formFields.${idx}.fieldOptions.internalTextPrompt`,
                                    "Please share some details about your experience.",
                                    { shouldDirty: true }
                                  );
                                }
                              }
                            }}
                            items={dropdownItems as any}
                            itemToStringLabel={item => FIELD_TYPE_MAP[item as FormFieldType]?.label ?? ""}
                            itemToIcon={item => {
                              if (typeof item === "string" && item.startsWith("__group_header__")) return null;
                              const Icon = FIELD_TYPE_MAP[item as FormFieldType]?.icon;
                              const itemColors = FIELD_TYPE_COLOR_MAP[item as FormFieldType];
                              return Icon ? (
                                <Icon
                                  className={cn(
                                    "size-3.5 shrink-0 mr-1",
                                    itemColors ? itemColors.icon : "text-primary"
                                  )}
                                />
                              ) : null;
                            }}
                            isItemEqualToValue={(a, b) => a === b}
                            searchable={false}
                            showClear={false}
                            className={cn(
                              "border-0 bg-transparent hover:bg-transparent shadow-none ring-0 focus-within:ring-0 focus:ring-0 focus:outline-none w-48 h-full min-h-0 text-[11px] font-bold pl-2 pr-0.5 cursor-pointer transition-all",
                              fieldError?.fieldType ? "text-destructive" : ""
                            )}
                          />
                        </div>
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Controller
                      name={`formFields.${idx}.fieldKey`}
                      control={control}
                      render={({ field: keyField, fieldState }) => (
                        <div className="flex flex-col items-end">
                          <div
                            className={cn(
                              "flex items-center gap-1.5 text-xs",
                              fieldState.invalid ? "text-destructive" : "text-slate-400"
                            )}
                          >
                            <span className="font-medium text-[11px] select-none">Key:</span>
                            <input
                              {...keyField}
                              placeholder="field_key"
                              className={cn(
                                "w-44 px-1.5 py-0.5 border rounded font-mono text-[10px] transition-colors focus:outline-none focus:ring-1",
                                fieldState.invalid
                                  ? "border-destructive bg-destructive/5 text-destructive focus:ring-destructive/30"
                                  : "border-slate-200 bg-slate-100/60 text-slate-600 focus:bg-white focus:ring-primary/30"
                              )}
                              title="Automatically generated identifier for integrations"
                            />
                          </div>
                          {fieldState.invalid && (
                            <span className="text-[9px] text-destructive mt-0.5 font-sans leading-none">
                              {fieldState.error?.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 p-1.5 rounded-lg transition-all duration-200"
                      title="Delete Question"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <Controller
                    name={`formFields.${idx}.title`}
                    control={control}
                    render={({ field: inputField, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <Field.Label className="text-xs font-semibold text-slate-500">Question Title</Field.Label>
                        <Input {...inputField} placeholder="Question Title" className="bg-white w-full font-medium" />
                        {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                      </Field>
                    )}
                  />

                  <Controller
                    name={`formFields.${idx}.subtitle`}
                    control={control}
                    render={({ field: inputField, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <Field.Label className="text-xs font-semibold text-slate-500">
                          Question Subtitle (Optional)
                        </Field.Label>
                        <Input {...inputField} placeholder="Question Subtitle" className="bg-white" />
                        {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                      </Field>
                    )}
                  />

                  {currentFieldType === "single_choice" && (
                    <Controller
                      name={`formFields.${idx}.fieldOptions.choices`}
                      control={control}
                      defaultValue={["Option 1", "Option 2"]}
                      render={({ field: choicesField }) => (
                        <ChoicesManager value={choicesField.value || []} onChange={choicesField.onChange} />
                      )}
                    />
                  )}

                  {currentFieldType === "star" && (
                    <StarFieldControls
                      idx={idx}
                      control={control}
                      setValue={setValue}
                      formFieldsValue={formFieldsValue}
                    />
                  )}

                  <div className="border-t border-slate-100 my-1" />

                  <div className="flex justify-end items-center gap-2">
                    <Controller
                      name={`formFields.${idx}.required`}
                      control={control}
                      render={({ field: switchField }) => (
                        <Switch checked={switchField.value} onCheckedChange={switchField.onChange} />
                      )}
                    />
                    <Label className="text-xs font-semibold text-slate-600">Required</Label>
                  </div>
                </div>
              </div>
            );
          })}

          {fields.length > 0 && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                className="w-full md:w-auto border-dashed border-2 hover:border-solid border-slate-300 hover:border-primary/60 hover:bg-primary/5 transition-all duration-200"
                type="button"
                onClick={addField}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add New Field
              </Button>
            </div>
          )}

          {fields.length === 0 && (
            <div className="text-center py-12 bg-muted/30 rounded-xl border-2 border-dashed border-slate-200">
              <Layout className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-sm font-medium text-slate-500">No fields added yet.</p>
              <p className="text-xs text-slate-400 mt-1">Click "Add Field" to start building your form.</p>
            </div>
          )}
        </div>
      </FormSection>

      <Dialog open={isReorderOpen} onOpenChange={setIsReorderOpen} size="md">
        <Dialog.Header>
          <Dialog.Title>Reorder Form Fields</Dialog.Title>
          <Dialog.Description>Drag and drop fields to rearrange their display order on your form.</Dialog.Description>
        </Dialog.Header>

        <Dialog.Content className="max-h-[450px] overflow-y-auto">
          <div className="space-y-3 p-1">
            {localFields.map((item, idx) => {
              const mapping = FIELD_TYPE_MAP[item.fieldType as FormFieldType];
              const FieldIcon = mapping?.icon || Layout;
              const isDragging = draggedIndex === idx;

              return (
                <div
                  key={item.id || idx}
                  draggable
                  onDragStart={e => handleDragStart(e, idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "flex items-center gap-3 p-3 bg-white border rounded-xl shadow-sm cursor-grab active:cursor-grabbing select-none transition-all duration-150",
                    isDragging
                      ? "opacity-55 scale-[0.98] border-primary/40 bg-primary/5 ring-2 ring-primary/10"
                      : "border-slate-200 hover:border-slate-300 hover:shadow-md"
                  )}
                >
                  <div className="text-slate-400 hover:text-slate-600 transition-colors">
                    <GripVertical className="size-4 shrink-0" />
                  </div>

                  <div className="p-1.5 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-500">
                    <FieldIcon className="size-4 shrink-0" />
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                    <span className="font-semibold text-slate-800 text-sm truncate font-sans">
                      {item.title || "Untitled Question"}
                    </span>
                    {item.fieldKey && (
                      <span className="font-mono text-xs text-slate-400 bg-slate-100/80 px-2 py-0.5 rounded border border-slate-200/50 truncate max-w-[150px] self-start sm:self-auto">
                        {item.fieldKey}
                      </span>
                    )}
                  </div>

                  <span className="text-xs font-bold text-slate-400 px-2 py-1 rounded bg-slate-50 border border-slate-100 uppercase tracking-wide shrink-0">
                    Q{idx + 1}
                  </span>
                </div>
              );
            })}
          </div>
        </Dialog.Content>

        <Dialog.Footer>
          <Button variant="outline" onClick={() => setIsReorderOpen(false)} type="button">
            Cancel
          </Button>
          <Button onClick={handleApplyOrder} type="button">
            OK
          </Button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
}
